"use client";
import type { StoreData } from "@components/store-locator/controller";
import { formatStreet } from "@components/store-locator/normalize-store";
import { activeSiteConfig, activeSiteCountry } from "@config/site/site-config";
import type {
    Image,
    Product,
    ProductVariant,
    ProductVariantAvailabilityWithChannel,
} from "@graphql/generated/types";
import { useStores } from "@hooks/useStores";
import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { useStore } from "@store/store";
import { Text } from "@ui/components/content/text/Text";
import { Spinner } from "@ui/components/feedback/spinner/Spinner";
import { Button } from "@ui/components/forms/button/Button";
import { Box } from "@ui/components/layout/box/Box";
import { Circle } from "@ui/components/layout/center/Circle";
import { Flex } from "@ui/components/layout/flex/Flex";
import { useDisclosure } from "@ui/hooks/useDisclosure";
import { isEmpty } from "lodash-es";
import dynamic from "next/dynamic";
import { useEffect, useMemo, useState } from "react";
import { isAnyVariantsInStock } from "../utils";
import { useLocale } from "next-intl";

const StoreLocatorModal = dynamic(() => import("@components/click-and-collect/StoreLocatorModal"), {
    ssr: false,
});

interface ClickAndCollectCardProps {
    product: Product;
    selectedVariant?: ProductVariant;
    onClickAndCollectClick?: () => void;
}

export interface LineItemStoreFinder {
    name: string;
    selectedVariant: ProductVariant;
    image: Image;
    descriptiveHeading?: string;
}

const yellowColor = "yellow.200";
const greenColor = "green.200";
const redColor = "red.200";

const ClickAndCollectCard = ({
    product,
    selectedVariant,
    onClickAndCollectClick,
}: ClickAndCollectCardProps) => {
    const { get: getMicrocopy, getMultiple } = useMicrocopy();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const getStoreData = useStore((state) => state.getStoreData);
    const storeData = getStoreData();
    const setClickAndCollectStores = useStore((state) => state.setClickAndCollectStores);

    const locale = useLocale();
    // We will rely on public store to filter out stores for country, C&C feature
    const country = activeSiteConfig.country;

    const [stockMessage, setStockMessage] = useState<string>("");
    const [isClient, setIsClient] = useState<boolean>(false);
    const [stockColor, setStockColor] = useState<string>(greenColor);

    const isAnyVariantInStock = isAnyVariantsInStock(product.masterData?.current.variants);
    const substringCountry = activeSiteCountry;

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.headline,
            ClickAndCollect.storeLocatorDescription,
            ClickAndCollect.availableSoon,
            ClickAndCollect.selectStore,
            ClickAndCollect.available,
            ClickAndCollect.changeStore,
            ClickAndCollect.homeDelivery,
            ClickAndCollect.unavailable,
        ]);
    })();

    const selectedVariantOutOfStock = selectedVariant?.availability?.channels?.results.every(
        (channel) => channel?.availability?.isOnStock !== true
    );

    const { data: storesData = [] } = useStores({
        country: country,
        locale: locale,
    });

    const storesWithPickup = useMemo(() => {
        // Filtering the stores that are available for PickupInStore in a market
        return storesData?.filter(
            (store) => store?.pickupInStore === true && store.countryCode === substringCountry
        );
    }, [storesData, substringCountry]);

    const storeWithStocksDictionary = (storesData: StoreData[]) => {
        const storesWithPickup = storesData?.filter((store) => store?.pickupInStore === true);

        const dictionary: Record<string, StoreData> = {};
        storesWithPickup?.forEach((item) => {
            dictionary[item?.supplyChannel?.[0].id] = item;
        });
        return dictionary;
    };

    const storesWithStock = useMemo(() => {
        if (selectedVariant) {
            const storeIdsWithStock = selectedVariant.availability.channels.results
                .filter((channel) => channel.availability.isOnStock)
                .map((channel) => channel.channel.id);

            return storesData?.filter((store) =>
                store?.supplyChannel?.some((channel) => storeIdsWithStock?.includes(channel.id))
            );
        }

        const storesWithStockValues = storeWithStocksDictionary(storesData);
        return product.masterData.current.variants.reduce((acc, variant) => {
            variant.availability.channels.results.forEach((result) => {
                if (result.channel.key !== substringCountry && result.availability.isOnStock) {
                    const store = storesWithStockValues[result.channel.id];
                    if (store && !acc.some((item) => item.storeId === store.storeId)) {
                        acc.push(store);
                    }
                }
            });
            return acc;
        }, []);
    }, [product, selectedVariant, storesData]);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const descriptiveHeading = getProductAttributeValue(
        product.masterData.current.masterVariant.attributesRaw,
        Attributes.descriptiveHeading,
        locale
    );

    // The data we will pass to the StoreLocator to generate the card with Price, Badges and DescriptiveHeading
    const selectedProductData: LineItemStoreFinder = {
        name: product.masterData.current.name,
        selectedVariant,
        image: product.masterData.current.masterVariant.images[0],
        descriptiveHeading,
    };

    const clickAndCollectHeadline = microcopies[ClickAndCollect.headline];
    const availableSoon = microcopies[ClickAndCollect.availableSoon];
    const availableText = microcopies[ClickAndCollect.available];

    const availableInStores = getMicrocopy(
        CommonCMS.page,
        storesWithStock?.length === 1
            ? ClickAndCollect.availableInStore
            : ClickAndCollect.availableInStores,
        {
            replacements: {
                storesWithStock: storesWithStock?.length,
            },
        }
    );

    const updateStock = (message: string, color: string) => {
        setStockMessage(message);
        setStockColor(color);
    };

    // Check and filter stores for C&C, check availability and change StockMessage based on variant against selected store (if any)
    useEffect(() => {
        if (
            !isAnyVariantInStock ||
            selectedVariant?.availability?.channels?.results?.length === 0
        ) {
            return (
                setStockMessage(microcopies[ClickAndCollect.unavailable]), setStockColor(redColor)
            );
        }
        if (selectedVariant) {
            const storesWithStockValues = storeWithStocksDictionary(storesData);
            const storesWithStockData: ProductVariantAvailabilityWithChannel[] = [];
            const variantStoresWithStock: StoreData[] = [];
            // Iterate over the entire SelectedVariant to pull out the SupplyChannels and compare with the values from stores
            const selectedVariantChannels = selectedVariant.availability?.channels?.results;
            selectedVariantChannels.forEach((channel) => {
                if (
                    channel.availability.isOnStock &&
                    channel.channel.key !== process.env.NEXT_PUBLIC_STORE_KEY &&
                    storesWithStockValues[channel.channel.id]
                ) {
                    variantStoresWithStock.push(storesWithStockValues[channel.channel.id]);
                    storesWithStockData?.push(channel);
                }
            });
            const isInSelectedStore = storesWithStockData.find(
                (store) =>
                    store.channel.id === storeData?.supplyChannel?.[0].id &&
                    store.availability.isOnStock
            );

            setClickAndCollectStores(variantStoresWithStock);
            if (isEmpty(storesWithStockData && storesWithStock)) {
                updateStock(availableSoon, yellowColor);
            } else if (!isEmpty(storeData)) {
                if (!isEmpty(isInSelectedStore)) {
                    updateStock(availableText, greenColor);
                } else {
                    updateStock(availableSoon, yellowColor);
                }
            } else {
                updateStock(availableInStores, greenColor);
            }
        } else {
            !isEmpty(storesWithStock)
                ? updateStock(availableInStores, greenColor)
                : updateStock(availableSoon, yellowColor);
        }
    }, [
        availableInStores,
        availableSoon,
        availableText,
        isAnyVariantInStock,
        microcopies,
        selectedVariant,
        storeData,
        storesData,
        storesWithStock,
    ]);

    const clickToCollect = () => {
        if (!selectedVariant) {
            onClickAndCollectClick();
            return;
        }
        if (selectedVariantOutOfStock || !storesWithPickup) {
            return;
        }
        onOpen();
    };

    return (
        <Box
            mt={1}
            px={4}
            py={4}
            bg="gray.50"
            onClick={clickToCollect}
            cursor="pointer"
            _hover={{
                bg: "gray.100",
            }}
        >
            <Flex
                gap={2}
                justifyContent="space-between"
                alignItems={"flex-start"}
                flexDirection={["column", "column", "row"]}
                fontSize={["mobileBodyTextNormal", "mobileBodyTextNormal", "desktopBodyTextNormal"]}
            >
                <Flex width={"100%"} justifyContent="space-between" alignItems={"start"}>
                    {!isEmpty(storeData) && isClient ? (
                        <Flex gap={2} flexDirection="column">
                            <Text color="black" fontWeight="bold">
                                {storeData.name}
                            </Text>
                            <Box>
                                <Text
                                    fontSize={[
                                        "mobileBodyTextNormal",
                                        "mobileBodyTextNormal",
                                        "desktopBodyTextNormal",
                                    ]}
                                    color="black"
                                >
                                    {formatStreet(storeData)}
                                </Text>
                                <Text
                                    fontSize={[
                                        "mobileBodyTextNormal",
                                        "mobileBodyTextNormal",
                                        "desktopBodyTextNormal",
                                    ]}
                                    color="black"
                                >
                                    {`${storeData.city}, ${storeData.postalCode}`}
                                </Text>
                            </Box>
                        </Flex>
                    ) : (
                        <Text fontWeight={"bold"} color="black">
                            {clickAndCollectHeadline}
                        </Text>
                    )}
                </Flex>
                {storesWithPickup ? (
                    <Flex
                        alignItems={"center"}
                        gap={1.5}
                        fontWeight="bold"
                        display="flex"
                        fontSize="desktopBodyTextNormal"
                    >
                        <Circle
                            size={2}
                            bg={selectedVariantOutOfStock ? redColor : stockColor}
                            // This value is as per design
                            border={stockColor === yellowColor && "0.8px solid black"}
                        />
                        <Text whiteSpace={"nowrap"} fontWeight="bold">
                            {selectedVariantOutOfStock
                                ? microcopies[ClickAndCollect.unavailable]
                                : stockMessage}
                        </Text>
                        {selectedVariant && !selectedVariantOutOfStock && (
                            <Button
                                fontWeight="bold"
                                variant={"link"}
                                h={"auto"}
                                display="flex"
                                fontSize={["mobileBodyTextSmall", "desktopBodyTextNormal"]}
                                onClick={onOpen}
                            >
                                {!isEmpty(storeData)
                                    ? microcopies[ClickAndCollect.changeStore]
                                    : microcopies[ClickAndCollect.selectStore]}
                            </Button>
                        )}
                    </Flex>
                ) : (
                    <Spinner />
                )}
            </Flex>
            {isOpen && (
                <StoreLocatorModal
                    isOpen={isOpen}
                    onClose={onClose}
                    selectedProductData={selectedProductData}
                    storesWithStock={storesWithPickup}
                    selectedVariant={selectedVariant}
                />
            )}
        </Box>
    );
};

export default ClickAndCollectCard;
